import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { ProductModule } from "@/modules/issues/types";

const key = (productId: string) => ["product-modules", productId] as const;

export function useProductModulesList(
  productId: string,
): UseQueryResult<ProductModule[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...key(productId), accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!productId,
    queryFn: async () => {
      const res = await apiFetch<{ modules: ProductModule[] }>(
        `/products/${productId}/modules`,
        { token: accessToken },
      );
      return res.modules;
    },
  });
}

export function useProductModuleMutations(productId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["product-modules", productId] });

  const createModule = useMutation({
    mutationFn: (name: string) =>
      apiFetch<{ module: ProductModule }>(`/products/${productId}/modules`, {
        method: "POST",
        body: { name },
        token: accessToken,
      }),
    onSuccess: invalidate,
  });

  const updateModule = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; isActive?: boolean };
    }) =>
      apiFetch<{ module: ProductModule }>(`/product-modules/${id}`, {
        method: "PATCH",
        body: data,
        token: accessToken,
      }),
    onSuccess: invalidate,
  });

  const deleteModule = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/product-modules/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: invalidate,
  });

  const createSubmodule = useMutation({
    mutationFn: ({ moduleId, name }: { moduleId: string; name: string }) =>
      apiFetch<{ module: ProductModule }>(
        `/product-modules/${moduleId}/submodules`,
        { method: "POST", body: { name }, token: accessToken },
      ),
    onSuccess: invalidate,
  });

  const updateSubmodule = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { name?: string; isActive?: boolean };
    }) =>
      apiFetch<{ module: ProductModule }>(`/product-submodules/${id}`, {
        method: "PATCH",
        body: data,
        token: accessToken,
      }),
    onSuccess: invalidate,
  });

  const deleteSubmodule = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/product-submodules/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: invalidate,
  });

  return {
    createModule,
    updateModule,
    deleteModule,
    createSubmodule,
    updateSubmodule,
    deleteSubmodule,
  };
}
